import { useEffect, useRef, useState } from 'react'
import HeartButton from './HeartButton'
import Avatar from './Avatar'

export default function Lightbox({ photos, index, onClose, onNavigate }) {
  const photo = photos[index]
  const touchStart = useRef(null)
  const [fullLoaded, setFullLoaded] = useState(false)

  // Each photo starts unloaded again, otherwise the next one inherits the last one's state
  // and shows a stale image as though it were already there.
  useEffect(() => setFullLoaded(false), [photo?.id])

  // Fetch the neighbours while this one is being looked at, so swiping lands on an image
  // that is already in cache rather than starting a 500KB download.
  useEffect(() => {
    ;[ photos[index - 1], photos[index + 1] ].forEach((neighbour) => {
      if (!neighbour || neighbour.kind === 'video' || !neighbour.fullUrl) return
      const preload = new Image()
      preload.src = neighbour.fullUrl
    })
  }, [index, photos])

  // Swiping between photos is how people expect a phone gallery to work.
  function onTouchStart(event) {
    // Ignore swipes that begin on the video element itself — those belong to its controls.
    if (event.target.tagName === 'VIDEO') {
      touchStart.current = null
      return
    }

    const touch = event.changedTouches[0]
    touchStart.current = { x: touch.clientX, y: touch.clientY }
  }

  function onTouchEnd(event) {
    if (!touchStart.current) return

    const touch = event.changedTouches[0]
    const dx = touch.clientX - touchStart.current.x
    const dy = touch.clientY - touchStart.current.y
    touchStart.current = null

    if (Math.abs(dx) < 50 || Math.abs(dx) < Math.abs(dy)) return
    onNavigate(dx < 0 ? index + 1 : index - 1)
  }

  useEffect(() => {
    function onKeyDown(event) {
      if (event.key === 'Escape') onClose()
      if (event.key === 'ArrowRight') onNavigate(index + 1)
      if (event.key === 'ArrowLeft') onNavigate(index - 1)
    }

    document.addEventListener('keydown', onKeyDown)
    document.body.style.overflow = 'hidden'

    return () => {
      document.removeEventListener('keydown', onKeyDown)
      document.body.style.overflow = ''
    }
  }, [index, onClose, onNavigate])

  if (!photo) return null

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={photo.caption || 'Photo'}
      className="fixed inset-0 z-50 flex flex-col bg-ink/80 pt-[env(safe-area-inset-top)] pb-[env(safe-area-inset-bottom)] backdrop-blur-md"
      onClick={onClose}
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
    >
      <div className="flex items-center justify-between gap-3 px-4 py-3 text-white sm:px-6">
        <span className="font-display font-bold">
          {index + 1} of {photos.length}
        </span>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          className="cursor-pointer rounded-full bg-white/15 px-4 py-2 font-display font-bold transition hover:bg-white/25"
        >
          Close ✕
        </button>
      </div>

      <div className="relative flex min-h-0 flex-1 items-center justify-center px-2 sm:px-6">
        {index > 0 && (
          <button
            type="button"
            aria-label="Previous photo"
            onClick={(event) => {
              event.stopPropagation()
              onNavigate(index - 1)
            }}
            className="absolute left-2 z-10 hidden h-14 w-14 cursor-pointer items-center justify-center rounded-full bg-white/20 text-2xl text-white transition hover:bg-white/35 sm:left-6 sm:flex"
          >
            ‹
          </button>
        )}

        {photo.kind === 'video' ? (
          <video
            key={photo.id}
            src={photo.videoUrl}
            poster={photo.thumbUrl || undefined}
            controls
            autoPlay
            playsInline
            preload="metadata"
            onClick={(event) => event.stopPropagation()}
            className="animate-pop-in max-h-full max-w-full rounded-2xl border-2 border-white/90 bg-black shadow-2xl sm:rounded-3xl sm:border-4"
          />
        ) : (
          // The grid has already downloaded the thumbnail, so it paints instantly and gives the
          // full-size image something to sharpen out of. It also sizes the frame, which keeps the
          // swap from reflowing when the real image lands.
          <div
            className="relative flex max-h-full max-w-full items-center justify-center overflow-hidden rounded-2xl border-2 border-white/90 shadow-2xl sm:rounded-3xl sm:border-4"
            onClick={(event) => event.stopPropagation()}
          >
            <img
              src={photo.thumbUrl || photo.fullUrl}
              alt=""
              aria-hidden="true"
              className={`max-h-full max-w-full object-contain transition-[filter] duration-300 ${
                fullLoaded ? 'blur-0' : 'blur-lg'
              }`}
            />

            <img
              src={photo.fullUrl}
              alt={photo.caption || 'Baby photo'}
              onLoad={() => setFullLoaded(true)}
              onError={() => setFullLoaded(true)}
              className={`absolute inset-0 h-full w-full object-contain transition-opacity duration-300 select-none ${
                fullLoaded ? 'opacity-100' : 'opacity-0'
              }`}
            />

            {!fullLoaded && (
              <span
                role="status"
                aria-label="Loading photo"
                className="absolute bottom-3 left-1/2 h-8 w-8 -translate-x-1/2 animate-spin rounded-full border-3 border-white/40 border-t-white"
              />
            )}
          </div>
        )}

        {index < photos.length - 1 && (
          <button
            type="button"
            aria-label="Next photo"
            onClick={(event) => {
              event.stopPropagation()
              onNavigate(index + 1)
            }}
            className="absolute right-2 z-10 hidden h-14 w-14 cursor-pointer items-center justify-center rounded-full bg-white/20 text-2xl text-white transition hover:bg-white/35 sm:right-6 sm:flex"
          >
            ›
          </button>
        )}
      </div>

      <div
        className="flex flex-wrap items-center justify-center gap-3 px-4 py-5 text-white sm:px-6"
        onClick={(event) => event.stopPropagation()}
      >
        <Avatar initials={photo.uploader.initials} size="sm" />
        <div className="max-w-xl text-center sm:text-left">
          {photo.caption && <p className="font-display text-lg font-bold">{photo.caption}</p>}
          <p className="text-sm text-white/70">
            {photo.uploader.name}
            {photo.uploader.relation ? ` · ${photo.uploader.relation}` : ''} · {photo.monthLabel}
          </p>
        </div>
        <HeartButton photo={photo} size="lg" />
      </div>
    </div>
  )
}
